/**
 * Runtime theme (docs/designs/mobile/CHAT_CHANGES.md, "Visual direction v3" and "Appearance").
 *
 * A person's style (calm or vibrant), main colour and colour scheme resolve to one token set. Components
 * read only these tokens, never a raw colour, so a theme change touches no component. Semantic colours
 * (status, priority, SLA risk, the ticket and bug types) are fixed in every theme so red always means
 * at risk.
 */
export const THEME_STYLES = ['calm', 'vibrant'];
export const ACCENTS = ['violet', 'ocean', 'teal', 'sunset', 'rose', 'graphite'];
export const TEXT_SIZES = ['small', 'default', 'large', 'larger'];
export const accents = {
    violet: { label: 'Chapsi violet', swatch: '#7C3AED', strong: '#7C3AED', strongHover: '#6D28D9', soft: '#F1EBFF', softText: '#6D28D9', barBase: '#1B1740', glow: [124, 58, 237], glowAlpha: 0.42, glow2: [255, 138, 61], glow2Alpha: 0.16, navRow: '#E4DBFF', navRowText: '#5B21B6', onDark: '#C4B5FD' },
    ocean: { label: 'Ocean', swatch: '#2563EB', strong: '#2563EB', strongHover: '#1D4ED8', soft: '#E8F0FF', softText: '#1D4ED8', barBase: '#0B1F3F', glow: [37, 99, 235], glowAlpha: 0.55, glow2: [13, 148, 136], glow2Alpha: 0.25, navRow: '#DCE7FF', navRowText: '#1E40AF', onDark: '#93C5FD' },
    teal: { label: 'Teal', swatch: '#0D9488', strong: '#0F766E', strongHover: '#115E59', soft: '#E6F6F4', softText: '#0F766E', barBase: '#082B2A', glow: [13, 148, 136], glowAlpha: 0.5, glow2: [37, 99, 235], glow2Alpha: 0.2, navRow: '#D3EFEB', navRowText: '#115E59', onDark: '#5EEAD4' },
    sunset: { label: 'Sunset', swatch: '#EA580C', strong: '#C2410C', strongHover: '#9A3412', soft: '#FFF1E8', softText: '#C2410C', barBase: '#2A1408', glow: [234, 88, 12], glowAlpha: 0.45, glow2: [240, 86, 140], glow2Alpha: 0.2, navRow: '#FFE2D1', navRowText: '#9A3412', onDark: '#FDBA74' },
    rose: { label: 'Rose', swatch: '#DB2777', strong: '#BE185D', strongHover: '#9D174D', soft: '#FCE7F3', softText: '#BE185D', barBase: '#2A0B1D', glow: [219, 39, 119], glowAlpha: 0.45, glow2: [255, 138, 61], glow2Alpha: 0.18, navRow: '#FBD7E8', navRowText: '#9D174D', onDark: '#F9A8D4' },
    graphite: { label: 'Graphite', swatch: '#334155', strong: '#334155', strongHover: '#1E293B', soft: '#EEF0F5', softText: '#334155', barBase: '#111827', glow: [100, 116, 139], glowAlpha: 0.45, glow2: [148, 163, 184], glow2Alpha: 0.15, navRow: '#E2E5EC', navRowText: '#1E293B', onDark: '#CBD5E1' },
};
/**
 * Fixed in every theme. [background, text]. The text is the darker shade of each hue (the same shades as
 * the web pills) so every pair is at least 4.5:1 (WCAG 2.2 AA, 1.4.3); contrast.test.ts checks them.
 * `risk` and `online` are for marks (bars, dots, borders, icons: 3:1); text in those meanings uses the
 * theme's `danger`, which lightens on dark sheets.
 */
export const semantic = {
    status: {
        backlog: ['#EEF0F5', '#475569'], todo: ['#EEF0F5', '#475569'], inProgress: ['#E8F0FF', '#2563EB'], review: ['#F1EBFF', '#7C3AED'],
        blocked: ['#FDE8EC', '#BE123C'], done: ['#EBF8EF', '#15803D'], open: ['#FDE8EC', '#BE123C'], waitingOnClient: ['#FEF3DC', '#B45309'], resolved: ['#EBF8EF', '#15803D'],
    },
    priority: { p1: ['#FDE8EC', '#BE123C'], p2: ['#FEF3DC', '#B45309'], p3: ['#EEF0F5', '#475569'] },
    risk: '#E11D48',
    online: '#16A34A',
};
const tint = {
    task: { bg: '#F1EBFF', fg: '#6D28D9' },
    support: { bg: '#FFF1E8', fg: '#C2410C' },
    bug: { bg: '#FDE8EC', fg: '#BE123C' },
    job: { bg: '#E8F0FF', fg: '#2563EB' },
};
const vivid = {
    task: 'linear-gradient(155deg, #8B3CF0 0%, #6D28D9 45%, #2563EB 100%)',
    support: 'linear-gradient(150deg, #FF7A3D 0%, #FF9A3D 30%, #F0568C 75%, #C04BF2 100%)',
    bug: 'linear-gradient(135deg, #F0568C, #E11D48)',
    job: 'linear-gradient(135deg, #22D3EE, #2563EB)',
};
/**
 * Vibrant stat tiles carry white figures and labels, so every stop is dark enough for 4.5:1 with white
 * (the icon-tile gradients above only carry icons and can be brighter).
 */
export const vibrantStatGradients = {
    violet: 'linear-gradient(155deg, #7C3AED 0%, #6D28D9 45%, #1D4ED8 100%)',
    orange: 'linear-gradient(135deg, #C2410C, #9A3412)',
    pink: 'linear-gradient(135deg, #BE185D, #9F1239)',
    blue: 'linear-gradient(135deg, #1D4ED8, #0E7490)',
};
/** Gradients survive only on sign-in, splash, the logo and Chapi (and Vibrant's tiles). */
export const brandGradient = 'linear-gradient(135deg, #FF8A3D 0%, #F05BB5 35%, #7C3AED 68%, #2563EB 100%)';
/** Sign-in and splash background, the same in every theme. */
export const splashBackground = 'radial-gradient(120% 70% at 105% -5%, rgba(124,58,237,.75) 0%, rgba(124,58,237,0) 55%), radial-gradient(80% 60% at -10% 0%, rgba(255,138,61,.55) 0%, rgba(255,138,61,0) 55%), radial-gradient(90% 60% at 70% 110%, rgba(37,99,235,.6) 0%, rgba(37,99,235,0) 60%), #0F172A';
/**
 * The dark sheets, read from UI-18 (docs/designs/v4/desktop/Chapsi-UI-18-ClientChannelDark.html): content and header
 * #141A2E, the right panel #10162A, hairlines #253050, ink #E7EAF3, body #CBD3E3, raised fills (segmented track) #1E2742.
 * The boards draw meta text in #64748B, which is 3.6:1 here, so muted text is #94A3B8 (6.7:1) and subtle text is the dark
 * navigation's muted #8B93A7 (5.6:1); contrast.ts checks both on every dark sheet.
 */
export const DARK = {
    content: '#141A2E',
    panel: '#10162A',
    raised: '#1E2742',
    border: '#253050',
    text: '#E7EAF3',
    body: '#CBD3E3',
    muted: '#94A3B8',
    subtle: '#8B93A7',
    /** The dark rail's inactive icons and labels (UI-18), 8.3:1 on the dark rail. */
    railIcon: '#A3ABBF',
    /** Tab count pills and progress tracks. */
    count: '#26304C',
    /** A message that mentions you (light: #FFF8EB). */
    mentionRow: '#2A2314',
};
const textScale = { small: 0.92, default: 1, large: 1.12, larger: 1.25 };
const rgba = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;
export function resolveTheme(input = {}) {
    const style = input.style ?? 'calm';
    const accent = input.accent ?? 'violet';
    const scheme = input.scheme ?? 'light';
    const a = accents[accent];
    const dark = scheme === 'dark';
    const topBar = style === 'vibrant'
        ? `radial-gradient(120% 70% at 105% -5%, ${rgba(a.glow, 0.75)} 0%, ${rgba(a.glow, 0)} 55%), radial-gradient(80% 60% at -10% 0%, rgba(255,138,61,.55) 0%, rgba(255,138,61,0) 55%), radial-gradient(90% 60% at 70% 110%, rgba(37,99,235,.6) 0%, rgba(37,99,235,0) 60%), #0F172A`
        : `radial-gradient(70% 90% at 100% 0%, ${rgba(a.glow, a.glowAlpha)} 0%, ${rgba(a.glow, 0)} 62%), radial-gradient(55% 70% at 0% 0%, ${rgba(a.glow2, a.glow2Alpha)} 0%, ${rgba(a.glow2, 0)} 60%), ${a.barBase}`;
    const topBarSolid = style === 'vibrant' ? '#0F172A' : a.barBase;
    const hex = ([r, g, b]) => `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
    const topBarLayers = style === 'vibrant'
        ? {
            base: '#0F172A',
            glows: [
                { cx: 1.05, cy: -0.05, rx: 1.2, ry: 0.7, color: hex(a.glow), opacity: 0.75, fadeAt: 0.55 },
                { cx: -0.1, cy: 0, rx: 0.8, ry: 0.6, color: '#FF8A3D', opacity: 0.55, fadeAt: 0.55 },
                { cx: 0.7, cy: 1.1, rx: 0.9, ry: 0.6, color: '#2563EB', opacity: 0.6, fadeAt: 0.6 },
            ],
        }
        : {
            base: a.barBase,
            glows: [
                { cx: 1, cy: 0, rx: 0.7, ry: 0.9, color: hex(a.glow), opacity: a.glowAlpha, fadeAt: 0.62 },
                { cx: 0, cy: 0, rx: 0.55, ry: 0.7, color: hex(a.glow2), opacity: a.glow2Alpha, fadeAt: 0.6 },
            ],
        };
    const tile = Object.fromEntries(Object.keys(tint).map((k) => [k, style === 'vibrant' ? { background: vivid[k], color: '#FFFFFF' } : { background: dark ? rgba(hexRgb(tint[k].fg), 0.2) : tint[k].bg, color: dark ? lighten(tint[k].fg) : tint[k].fg }]));
    const statColors = { violet: '#7C3AED', orange: '#FF8A3D', pink: '#F0568C', blue: '#2563EB' };
    const statGradients = vibrantStatGradients;
    const statTile = Object.fromEntries(Object.keys(statColors).map((k) => [
        k,
        style === 'vibrant'
            ? { background: statGradients[k], color: '#FFFFFF', dot: '#FFFFFF' }
            : { background: dark ? DARK.raised : '#FFFFFF', color: dark ? DARK.text : '#0F172A', dot: statColors[k] },
    ]));
    const card = (bg, border, color) => (dark ? { background: rgba(hexRgb(color), 0.14), border: rgba(hexRgb(color), 0.3), color: lighten(color) } : { background: bg, border, color });
    return {
        style,
        accent,
        scheme,
        fontScale: textScale[input.textSize ?? 'default'],
        topBar,
        topBarSolid,
        topBarLayers,
        primary: a.strong,
        primaryHover: a.strongHover,
        onPrimary: '#FFFFFF',
        accentSoft: dark ? rgba(hexRgb(a.swatch), 0.22) : a.soft,
        accentSoftText: dark ? a.onDark : a.softText,
        link: dark ? a.onDark : a.softText,
        badge: a.strong,
        tabActive: dark ? rgba(hexRgb(a.swatch), 0.22) : a.soft,
        canvas: dark ? DARK.panel : '#F6F7FB',
        surface: dark ? DARK.content : '#FFFFFF',
        surfaceAlt: dark ? DARK.panel : '#F6F7FB',
        border: dark ? DARK.border : '#E6E8F0',
        text: dark ? DARK.text : '#0F172A',
        textMuted: dark ? DARK.muted : '#5E6D83',
        textSubtle: dark ? DARK.subtle : '#627288',
        danger: dark ? '#FB7185' : '#BE123C',
        warn: dark ? '#FDBA74' : '#C2410C',
        frostedBar: rgba(hexRgb(topBarSolid), 0.78),
        frostedComposer: dark ? 'rgba(20,26,46,.72)' : 'rgba(255,255,255,.72)',
        tile,
        statTile,
        navRow: dark ? a.strong : a.navRow,
        navRowText: dark ? '#FFFFFF' : a.navRowText,
        eventCard: {
            ticket: card('#FFF7F0', '#FFE0C7', '#C2410C'),
            task: card('#F7F3FF', '#E4D8FF', '#6D28D9'),
            bug: card('#FFF5F7', '#FBD0DA', '#BE123C'),
            system: card('#F6F7FB', '#E6E8F0', '#475569'),
        },
    };
}
/** The theme as CSS custom properties, for the web app to set on <html>. */
export function themeCssVars(t) {
    const vars = {
        '--t-top-bar': t.topBar,
        '--t-top-bar-solid': t.topBarSolid,
        '--t-primary': t.primary,
        '--t-primary-hover': t.primaryHover,
        '--t-on-primary': t.onPrimary,
        '--t-accent-soft': t.accentSoft,
        '--t-accent-soft-text': t.accentSoftText,
        '--t-link': t.link,
        '--t-badge': t.badge,
        '--t-tab-active': t.tabActive,
        '--t-canvas': t.canvas,
        '--t-surface': t.surface,
        '--t-surface-alt': t.surfaceAlt,
        '--t-border': t.border,
        '--t-text': t.text,
        '--t-text-muted': t.textMuted,
        '--t-text-subtle': t.textSubtle,
        '--t-frosted-bar': t.frostedBar,
        '--t-frosted-composer': t.frostedComposer,
        '--t-font-scale': String(t.fontScale),
        '--t-nav-row': t.navRow,
        '--t-nav-row-text': t.navRowText,
    };
    for (const [k, v] of Object.entries(t.tile)) {
        vars[`--t-tile-${k}-bg`] = v.background;
        vars[`--t-tile-${k}-fg`] = v.color;
    }
    for (const [k, v] of Object.entries(t.statTile)) {
        vars[`--t-stat-${k}-bg`] = v.background;
        vars[`--t-stat-${k}-fg`] = v.color;
        vars[`--t-stat-${k}-dot`] = v.dot;
    }
    for (const [k, v] of Object.entries(t.eventCard)) {
        vars[`--t-event-${k}-bg`] = v.background;
        vars[`--t-event-${k}-border`] = v.border;
        vars[`--t-event-${k}-fg`] = v.color;
    }
    return vars;
}
function hexRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/**
 * A light pill ([pale background, dark text], e.g. `semantic.status.done`) for a dark sheet (Prompt 7: no pale chips showing
 * through on dark): the text hue at 20% over the sheet, with the text lightened 55% towards white. The phone's Pill uses it;
 * contrast.ts checks every semantic pill this way on every dark sheet.
 */
export function toneOnDark([, fg]) {
    return [rgba(hexRgb(fg), 0.2), lighten(fg, 0.55)];
}
/** A lighter version of a colour for text on dark sheets (mix 45% towards white by default). */
function lighten(hex, amount = 0.45) {
    const [r, g, b] = hexRgb(hex);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}
/** WCAG contrast ratio between two #rrggbb colours. */
export function contrastRatio(a, b) {
    const lum = (hex) => {
        const [r, g, bl] = hexRgb(hex).map((c) => {
            const s = c / 255;
            return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
        });
        return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
    };
    const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
//# sourceMappingURL=theme.js.map